/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Shared plumbing for the hand-built Real Estate screens.
 *
 * Everything talks to `/re/*` through the axiosInstance from useAuth() and
 * waits for `isAuthReady && isAuthenticated` before the first request (see
 * CLAUDE.md: during the #auth handoff the token lands before /verify does).
 *
 * Contract: docs/real-estate/api-contract.md. List endpoints return
 * `{items, total, page, page_size}`; business errors come back as
 * `{detail: {error_code, message}}`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'auth/Auth';
import { getApiErrorMessage } from 'utils/apiErrors';
import {
  bookingFlowCodes, bookingStatusCodes, buyerCategoryOptions, defaultCurrency, reLocale, rentFrequencyOptions,
} from '../variant';

// ---------------------------------------------------------------------------
// Vocabularies (mirror backend/app/real_estate/models.py)
// ---------------------------------------------------------------------------

export const PROJECT_STATUSES = ['PLANNED', 'LAUNCHED', 'UNDER_CONSTRUCTION', 'COMPLETED', 'HANDED_OVER', 'ON_HOLD'];
export const UNIT_STATUSES = ['AVAILABLE', 'HELD', 'RESERVED', 'BOOKED', 'SOLD', 'HANDED_OVER', 'LEASED', 'BLOCKED'];
export const UNIT_CATEGORIES = ['APARTMENT', 'VILLA', 'TOWNHOUSE', 'PENTHOUSE', 'DUPLEX', 'PLOT', 'OFFICE', 'RETAIL', 'WAREHOUSE'];
export const VIEWING_STATUSES = ['SCHEDULED', 'CONFIRMED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'];
export const VIEWING_TYPES = ['SITE_VISIT', 'SHOW_FLAT', 'UNIT', 'VIRTUAL'];
export const INTEREST_LEVELS = ['HOT', 'WARM', 'COLD'];
export const HOLD_STATUSES = ['ACTIVE', 'CONVERTED', 'EXPIRED', 'RELEASED'];
// UAE lists. The tenant's own come from its variant: bookingStatuses(),
// bookingFlow(), rentFrequencies(), buyerCategories() below.
export const BOOKING_STATUSES = ['DRAFT', 'RESERVED', 'SPA_ISSUED', 'SPA_SIGNED', 'REGISTERED', 'HANDED_OVER', 'CANCELLED'];
export const BOOKING_FLOW = ['DRAFT', 'RESERVED', 'SPA_ISSUED', 'SPA_SIGNED', 'REGISTERED', 'HANDED_OVER'];
export const bookingStatuses = () => bookingStatusCodes();
export const bookingFlow = () => bookingFlowCodes();
export const rentFrequencies = () => rentFrequencyOptions().map((o) => o.value);
export const buyerCategories = () => buyerCategoryOptions();
export const BOOKING_PARTY_ROLES = ['PRIMARY_BUYER', 'JOINT_BUYER', 'POA', 'GUARANTOR', 'NOMINEE'];
export const MILESTONE_TRIGGERS = [
  'ON_BOOKING', 'DAYS_AFTER_BOOKING', 'FIXED_DATE', 'CONSTRUCTION_PCT', 'ON_HANDOVER', 'MONTHS_AFTER_HANDOVER',
];
export const INSTALLMENT_STATUSES = ['PENDING_TRIGGER', 'SCHEDULED', 'DUE', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'WAIVED', 'CANCELLED'];
export const COMMISSION_PAYEE_TYPES = ['BROKER_FIRM', 'BROKER_AGENT', 'INTERNAL_AGENT', 'CO_BROKER'];
export const COMMISSION_STATUSES = ['ACCRUED', 'ELIGIBLE', 'APPROVED', 'PAID', 'CLAWED_BACK', 'CANCELLED'];
export const PAYOUT_STATUSES = ['DRAFT', 'APPROVED', 'PAID', 'CANCELLED'];
export const LEASE_STATUSES = ['DRAFT', 'ACTIVE', 'RENEWAL_DUE', 'RENEWED', 'TERMINATED', 'EXPIRED'];
export const LEASE_PARTY_ROLES = ['TENANT', 'OCCUPANT', 'LANDLORD', 'GUARANTOR'];
export const RENT_FREQUENCIES = ['MONTHLY', 'QUARTERLY', 'SEMI_ANNUAL', 'ANNUAL'];
export const CHEQUE_STATUSES = ['PENDING', 'DEPOSITED', 'CLEARED', 'BOUNCED', 'REPLACED', 'CANCELLED'];
export const MORTGAGE_STAGES = [
  'ENQUIRY', 'DOCUMENTS', 'PRE_APPROVAL', 'VALUATION', 'FINAL_OFFER', 'DISBURSED', 'DECLINED', 'WITHDRAWN',
];
export const RESIDENCY_STATUSES = ['UAE_NATIONAL', 'RESIDENT', 'NON_RESIDENT'];
export const EMPLOYMENT_TYPES = ['SALARIED', 'SELF_EMPLOYED'];
export const RATE_TYPES = ['FIXED', 'VARIABLE'];
export const PAYMENT_MODES = ['BANK_TRANSFER', 'CHEQUE', 'CARD', 'CASH', 'ESCROW'];

export const TRIGGER_LABELS = {
  ON_BOOKING: 'On booking',
  DAYS_AFTER_BOOKING: 'Days after booking',
  FIXED_DATE: 'Fixed date',
  CONSTRUCTION_PCT: 'Construction %',
  ON_HANDOVER: 'On handover',
  MONTHS_AFTER_HANDOVER: 'Months after handover',
};

/** SPA_SIGNED → "Spa Signed"; keeps well-known acronyms upper-case. */
const ACRONYMS = new Set(['SPA', 'DLD', 'RERA', 'EOI', 'POA', 'LTV', 'DBR', 'UAE', 'NOC', 'BUA']);
export const humanize = (value) => {
  if (value === null || value === undefined || value === '') return '';
  return String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');
};

export const toOptions = (values) => values.map((v) => ({ value: v, label: humanize(v) }));

// ---------------------------------------------------------------------------
// Formatting, in the tenant's country (variant.js: en-AE / AED by default)
// ---------------------------------------------------------------------------

const moneyFormatters = {};
const getMoneyFormatter = (currency, digits) => {
  const locale = reLocale();
  const key = `${locale}|${currency}|${digits}`;
  if (!moneyFormatters[key]) {
    try {
      moneyFormatters[key] = new Intl.NumberFormat(locale, {
        style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits,
      });
    } catch (e) {
      moneyFormatters[key] = new Intl.NumberFormat(locale, {
        style: 'currency', currency: defaultCurrency(), minimumFractionDigits: digits, maximumFractionDigits: digits,
      });
    }
  }
  return moneyFormatters[key];
};

export const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/** AED 1,250,000.00 (A$ in Australia). `decimals: 0` for tiles, `compact` for charts (AED 1.3M). */
export const formatMoney = (value, currency = null, { decimals = 2, compact = false } = {}) => {
  const n = toNumber(value);
  if (n === null) return '—';
  if (compact) {
    const abs = Math.abs(n);
    const cur = currency || defaultCurrency();
    if (abs >= 1e9) return `${cur} ${(n / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${cur} ${(n / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${cur} ${(n / 1e3).toFixed(1)}K`;
  }
  return getMoneyFormatter(currency || defaultCurrency(), decimals).format(n);
};

export const formatNumber = (value, decimals = 0) => {
  const n = toNumber(value);
  if (n === null) return '—';
  return n.toLocaleString(reLocale(), { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

export const formatPct = (value, decimals = 1) => {
  const n = toNumber(value);
  if (n === null) return '—';
  return `${n.toFixed(decimals)}%`;
};

const parseDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  // YYYY-MM-DD must be read as a local date, not UTC midnight.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDate = (value) => {
  const d = parseDateOnly(value);
  if (!d) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value) => {
  const d = parseDateOnly(value);
  if (!d) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const pad = (n) => String(n).padStart(2, '0');
/** Local date as YYYY-MM-DD. */
export const isoDate = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayIso = () => isoDate(new Date());
export const addDaysIso = (days, from = new Date()) => {
  const d = new Date(from);
  d.setDate(d.getDate() + days);
  return isoDate(d);
};
/** For <input type="datetime-local"> */
export const toLocalInputDateTime = (value) => {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  return `${isoDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
/** datetime-local value → ISO 8601 with the browser's offset. */
export const localInputToIso = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export const bedroomsLabel = (b) => {
  if (b === null || b === undefined || b === '') return '—';
  return Number(b) === 0 ? 'Studio' : `${b} BR`;
};

export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const ERROR_MESSAGES = {
  MODULE_NOT_ENTITLED: 'Your workspace is not licensed for this Real Estate module.',
  UNIT_NOT_AVAILABLE: 'This unit is no longer available. It may have just been held or booked by someone else.',
  PLAN_PERCENTAGES_MUST_TOTAL_100: 'Payment plan milestones must add up to exactly 100%.',
  INVALID_STATUS_TRANSITION: 'That status change is not allowed from the current status.',
  IN_USE: 'This record is in use by other records and cannot be deleted.',
  HOLD_NOT_ACTIVE: 'This hold is no longer active.',
  SCHEDULE_HAS_PAYMENTS: 'The schedule cannot be regenerated after a payment has been recorded.',
  PAYMENT_EXCEEDS_OUTSTANDING: 'The payment is larger than the amount outstanding on this installment.',
  ENTRIES_NOT_APPROVED: 'Only APPROVED commission entries can be paid out.',
  MIXED_PAYEES: 'A payout can only contain entries for one payee.',
  COMMISSION_NOT_ELIGIBLE: 'This commission is not yet eligible for approval.',
  LEASE_OVERLAP: 'The unit already has a lease covering these dates.',
  UNIT_NOT_LEASABLE: 'This unit cannot be leased in its current status.',
  NOT_FOUND: 'The record was not found. It may have been deleted.',
};

export const getErrorCode = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) return detail.error_code || null;
  return null;
};

/** Human message for an axios error, preferring our error_code copy. */
export const errorMessage = (error, fallback = 'Something went wrong') => {
  const code = getErrorCode(error);
  if (code && ERROR_MESSAGES[code]) {
    const serverMsg = error?.response?.data?.detail?.message;
    // Keep specific server context (e.g. which unit) when it adds something.
    return serverMsg && serverMsg !== code ? `${ERROR_MESSAGES[code]} (${serverMsg})` : ERROR_MESSAGES[code];
  }
  if (error?.response?.status === 404) return ERROR_MESSAGES.NOT_FOUND;
  return getApiErrorMessage(error, fallback);
};

// ---------------------------------------------------------------------------
// Params / CSV helpers
// ---------------------------------------------------------------------------

export const cleanParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
      return;
    }
    out[k] = v;
  });
  return out;
};

const csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** columns: [{key, label, value?: (row) => any}] */
export const downloadCsv = (filename, columns, rows) => {
  const header = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => csvCell(c.value ? c.value(r) : r[c.key])).join(','));
  const blob = new Blob([[header, ...body].join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** axiosInstance plus the "may I call yet" flag. */
export const useReApi = () => {
  const { axiosInstance, isAuthReady, isAuthenticated, user } = useAuth();
  return { api: axiosInstance, ready: Boolean(isAuthReady && isAuthenticated), user };
};

/**
 * Paginated list against a contract list endpoint.
 *
 *   const list = useReList('/re/bookings', { status, project_id });
 *   list.items, list.total, list.page, list.setPage, list.pageSize, list.setPageSize, list.reload()
 *
 * `params` may be a new object every render; it is compared by value.
 * Pass `path = null` to disable.
 */
export const useReList = (path, params = {}, { pageSize: initialPageSize = 25, enabled = true } = {}) => {
  const { api, ready } = useReApi();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: null });
  const [tick, setTick] = useState(0);
  const paramsKey = JSON.stringify(cleanParams(params));
  const lastParamsKey = useRef(paramsKey);

  // A filter change goes back to page 1.
  useEffect(() => {
    if (lastParamsKey.current !== paramsKey) {
      lastParamsKey.current = paramsKey;
      setPage(1);
    }
  }, [paramsKey]);

  useEffect(() => {
    if (!ready || !path || !enabled) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    api
      .get(path, { params: { ...JSON.parse(paramsKey), page, page_size: pageSize } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        const items = Array.isArray(data) ? data : data.items || [];
        const total = Array.isArray(data) ? data.length : data.total ?? items.length;
        setState({ items, total, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ items: [], total: 0, loading: false, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [api, ready, path, enabled, paramsKey, page, pageSize, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const setItems = useCallback((updater) => {
    setState((s) => ({ ...s, items: typeof updater === 'function' ? updater(s.items) : updater }));
  }, []);

  return {
    ...state,
    page,
    setPage,
    pageSize,
    setPageSize: (n) => {
      setPageSize(n);
      setPage(1);
    },
    reload,
    setItems,
  };
};

/** Single GET. `path = null` disables. */
export const useReGet = (path, params) => {
  const { api, ready } = useReApi();
  const [state, setState] = useState({ data: null, loading: Boolean(path), error: null });
  const [tick, setTick] = useState(0);
  const paramsKey = JSON.stringify(cleanParams(params || {}));

  useEffect(() => {
    if (!ready || !path) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    api
      .get(path, { params: JSON.parse(paramsKey) })
      .then((res) => {
        if (!cancelled) setState({ data: res.data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err });
      });
    return () => {
      cancelled = true;
    };
  }, [api, ready, path, paramsKey, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const setData = useCallback((updater) => {
    setState((s) => ({ ...s, data: typeof updater === 'function' ? updater(s.data) : updater }));
  }, []);
  return { ...state, reload, setData };
};

/**
 * Reference options for pickers: first page (max 200) of a list endpoint.
 * Returns `[]` quietly on error (e.g. MODULE_NOT_ENTITLED for brokerage).
 */
export const useReOptions = (path, params) => {
  const { api, ready } = useReApi();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const paramsKey = JSON.stringify(cleanParams(params || {}));

  useEffect(() => {
    if (!ready || !path) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get(path, { params: { page: 1, page_size: 200, ...JSON.parse(paramsKey) } })
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        setOptions(Array.isArray(data) ? data : data.items || []);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, ready, path, paramsKey]);

  return { options, loading };
};

/** Core CRM users, for "agent" / "assigned to" pickers. */
export const useUsers = () => {
  const { api, ready } = useReApi();
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    api
      .get('/users/')
      .then((res) => {
        if (!cancelled) setUsers(Array.isArray(res.data) ? res.data : res.data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [api, ready]);
  return users;
};

export const userLabel = (u) => {
  if (!u) return '';
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return full || u.username || u.email || `User #${u.id}`;
};

export const contactLabel = (c) => {
  if (!c) return '';
  const full = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return full || c.name || c.email || c.phone || `Contact #${c.id}`;
};

/** Stable in-component memo of an options array keyed by id. */
export const useById = (list) => useMemo(() => {
  const map = {};
  (list || []).forEach((x) => {
    map[x.id] = x;
  });
  return map;
}, [list]);

// ---------------------------------------------------------------------------
// Lease cheque schedule (client-side preview)
// ---------------------------------------------------------------------------

// Mirrors backend leasing_service.term_months / build_rent_schedule exactly,
// so the preview and the saved schedule agree: whole months only (a partial
// month is dropped, as relativedelta does), end date inclusive.
const monthsBetween = (startIso, endIso) => {
  const s = parseDateOnly(startIso);
  const e = parseDateOnly(endIso);
  if (!s || !e || e <= s) return 0;
  // End dates are inclusive (1 Jan → 31 Dec is 12 months), so count to the day after.
  const end = new Date(e);
  end.setDate(end.getDate() + 1);
  let months = (end.getFullYear() - s.getFullYear()) * 12 + (end.getMonth() - s.getMonth());
  if (end.getDate() < s.getDate()) months -= 1;
  return Math.max(1, months);
};

// Whole months, clamped to the last day of the target month (relativedelta).
const addMonths = (date, months) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
};

/**
 * Preview of the rent cheques a lease will get.
 *
 * Rent for the term is annual_rent × months/12 (exactly annual_rent for a
 * one-year tenancy), split into `numberOfCheques` equal cheques spaced evenly
 * from the start date; the last cheque absorbs the rounding. The backend
 * builds the real schedule on POST /re/leases — this is only a preview.
 */
export const computeRentSchedule = ({ startDate, endDate, annualRent, numberOfCheques }) => {
  const rent = toNumber(annualRent);
  const n = parseInt(numberOfCheques, 10);
  const months = monthsBetween(startDate, endDate);
  if (!rent || !n || n < 1 || !months) return { lines: [], total: 0, months };
  const total = round2((rent * months) / 12);
  const each = round2(total / n);
  const start = parseDateOnly(startDate);
  const lines = [];
  for (let i = 0; i < n; i += 1) {
    const amount = i === n - 1 ? round2(total - each * (n - 1)) : each;
    lines.push({ sequence: i + 1, due_date: isoDate(addMonths(start, Math.floor((months * i) / n))), amount });
  }
  return { lines, total, months };
};

/**
 * Preview of an Australian lease's rent lines (bond model): one line per rent
 * period starting on or before the end date, each annual_rent / periods per
 * year; a short last period is charged by the day at annual_rent / 365.
 * Mirrors backend leasing_service.build_periodic_schedule.
 */
const PERIODS = { WEEKLY: [52, 7], FORTNIGHTLY: [26, 14], MONTHLY: [12, null] };
export const computePeriodicSchedule = ({ startDate, endDate, annualRent, frequency }) => {
  const rent = toNumber(annualRent);
  const start = parseDateOnly(startDate);
  const period = PERIODS[frequency];
  if (!rent || !start || !period) return { lines: [], total: 0 };
  let last = parseDateOnly(endDate);
  if (!last) {
    last = addMonths(start, 12);
    last.setDate(last.getDate() - 1);
  }
  if (last <= start) return { lines: [], total: 0 };
  const [perYear, days] = period;
  const step = (i) => {
    if (days === null) return addMonths(start, i);
    const d = new Date(start);
    d.setDate(d.getDate() + days * i);
    return d;
  };
  const each = round2(rent / perYear);
  const lines = [];
  let due = start;
  while (due <= last) {
    const next = step(lines.length + 1);
    const lastOfPeriod = new Date(next);
    lastOfPeriod.setDate(lastOfPeriod.getDate() - 1);
    const amount = lastOfPeriod <= last
      ? each
      : round2((rent / 365) * (Math.round((last - due) / 86400000) + 1));
    lines.push({ sequence: lines.length + 1, due_date: isoDate(due), amount });
    due = next;
  }
  return { lines, total: round2(lines.reduce((sum, l) => sum + l.amount, 0)) };
};

/** Cheques implied by a frequency over a one-year lease. */
export const chequesForFrequency = (freq) => ({ MONTHLY: 12, QUARTERLY: 4, SEMI_ANNUAL: 2, ANNUAL: 1 }[freq] || 1);

// ---------------------------------------------------------------------------
// Mortgage maths (display only; the backend computes the stored values)
// ---------------------------------------------------------------------------

export const computeEmi = (principal, annualRatePct, months) => {
  const p = toNumber(principal);
  const r = toNumber(annualRatePct);
  const n = parseInt(months, 10);
  if (!p || !n) return null;
  if (!r) return round2(p / n);
  const mr = r / 100 / 12;
  return round2((p * mr * (1 + mr) ** n) / ((1 + mr) ** n - 1));
};

export const computeLtv = (loan, value) => {
  const l = toNumber(loan);
  const v = toNumber(value);
  if (!l || !v) return null;
  return round2((l / v) * 100);
};

/** DBR = (existing liabilities + new EMI) / monthly income. UAE cap 50%. */
export const computeDbr = (income, liabilities, emi) => {
  const i = toNumber(income);
  if (!i) return null;
  return round2((((toNumber(liabilities) || 0) + (toNumber(emi) || 0)) / i) * 100);
};
